import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Upload, Search, PackagePlus, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { justoAPI } from '@/lib/justo';
import { recuentosAPI, downloadBlob, errorMsg, fmtNum, parseCantidad } from '@/lib/recuentos';
import { fold } from '@/lib/justoSearch';
import { EquivalenciaRow } from './EquivalenciaRow';

const UploadResult = ({ result, onClose }) => (
  <Dialog open={!!result} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="eq-upload-result">
      <DialogHeader><DialogTitle>Resultado de la carga</DialogTitle></DialogHeader>
      {result && (
        <div className="space-y-2 text-sm">
          <p>{result.guardados} equivalencias guardadas · {result.sin_cambios} filas vacías sin cambios</p>
          {result.errores.length > 0 ? (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3" data-testid="eq-upload-errores">
              <p className="font-semibold text-red-700 mb-1">{result.errores.length} filas con error (no se guardaron):</p>
              <ul className="space-y-0.5 text-red-700">{result.errores.map((e) => <li key={e.fila}>Fila {e.fila} ({e.codigo}): {e.error}</li>)}</ul>
            </div>
          ) : <p className="text-green-700">Sin errores.</p>}
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const FileButton = ({ label, icon: Icon, onFile, testId }) => {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept=".xlsx" className="hidden" data-testid={`${testId}-input`}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} />
      <Button variant="outline" onClick={() => ref.current?.click()} data-testid={testId}><Icon className="w-4 h-4 mr-1" /> {label}</Button>
    </>
  );
};

const base = (r) => ({ id_justo: r.id_justo, factor: fmtNum(r.factor), no_exportar: r.no_exportar });
const isDirty = (r, v) => !!v && (v.id_justo !== r.id_justo || v.factor !== fmtNum(r.factor) || v.no_exportar !== r.no_exportar);
const FILTROS = [['todos', 'Todos'], ['sin', 'Solo sin equivalencia'], ['con', 'Solo con equivalencia']];

const validar = (v) => {
  const factor = parseCantidad(v.factor);
  if (!v.no_exportar && (!v.id_justo || !factor || Number.isNaN(factor))) return null;
  return { id_justo: v.id_justo, factor: Number.isNaN(factor) ? null : factor, no_exportar: v.no_exportar };
};

export const EquivalenciasTab = () => {
  const [data, setData] = useState({ items: [], sin_equivalencia: 0 });
  const [catalogo, setCatalogo] = useState([]);
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadEq = () => justoAPI.equivalencias().then((e) => setData(e.data));
  const loadCatalogo = () => recuentosAPI.ingredientes()
    .then((c) => setCatalogo([...c.data].sort((a, b) => a.nombre_justo.localeCompare(b.nombre_justo, 'es'))));
  useEffect(() => { loadEq(); loadCatalogo(); }, []);

  const rows = useMemo(() => {
    const t = fold(search.trim());
    return data.items.filter((r) => (filtro === 'todos' || (filtro === 'sin') === r.sin_equivalencia)
      && (!t || fold(`${r.codigo_tupu} ${r.nombre_tupu}`).includes(t)));
  }, [data, search, filtro]);
  const dirtyIds = data.items.filter((r) => isDirty(r, edits[r.item_id])).map((r) => r.item_id);

  const saveMany = async (ids) => {
    const byId = Object.fromEntries(data.items.map((r) => [r.item_id, r]));
    const invalid = ids.filter((id) => !validar(edits[id]));
    if (invalid.length) toast.error(`${invalid.length} ${invalid.length === 1 ? 'fila' : 'filas'} sin ingrediente o con factor inválido: ${invalid.map((id) => byId[id].codigo_tupu).join(', ')}`);
    const ok = ids.filter((id) => validar(edits[id]));
    if (!ok.length) return;
    setSaving(true);
    const res = await Promise.allSettled(ok.map((id) => justoAPI.updateEquivalencia(id, validar(edits[id]))));
    const saved = ok.filter((_, i) => res[i].status === 'fulfilled');
    const failed = ok.filter((_, i) => res[i].status === 'rejected');
    await loadEq();
    setEdits((e) => { const n = { ...e }; saved.forEach((id) => delete n[id]); return n; });
    if (saved.length) toast.success(`${saved.length} ${saved.length === 1 ? 'equivalencia guardada' : 'equivalencias guardadas'}`);
    if (failed.length) toast.error(`Error al guardar: ${failed.map((id) => byId[id].codigo_tupu).join(', ')}`);
    setSaving(false);
  };

  const descargar = async () => downloadBlob((await justoAPI.descargarEquivalencias()).data, 'equivalencias_justo.xlsx');
  const subir = async (file) => {
    try { setResult((await justoAPI.subirEquivalencias(file)).data); loadEq(); }
    catch (e) { toast.error(errorMsg(e, 'Error al subir')); }
  };
  const importarCatalogo = async (file) => {
    try {
      const { data: d } = await recuentosAPI.importarIngredientes(file);
      toast.success(`Catálogo Justo: ${d.nuevos} ingredientes nuevos, ${d.existentes_sin_cambios} ya existían`);
      loadCatalogo(); loadEq();
    } catch (e) { toast.error(errorMsg(e, 'Error al importar')); }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-sm font-semibold px-3 py-1.5 rounded-lg ${data.sin_equivalencia ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-testid="eq-sin-count">
          {data.sin_equivalencia ? `${data.sin_equivalencia} productos sin equivalencia` : 'Todos los productos tienen equivalencia'}
        </span>
        <span className="text-xs text-slate-400" data-testid="eq-catalogo-count">Catálogo Justo: {catalogo.length} ingredientes</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" onClick={descargar} data-testid="eq-descargar-btn"><Download className="w-4 h-4 mr-1" /> Descargar equivalencias (Excel)</Button>
          <FileButton label="Subir equivalencias (Excel)" icon={Upload} onFile={subir} testId="eq-subir-btn" />
          <FileButton label="Importar ingredientes Justo" icon={PackagePlus} onFile={importarCatalogo} testId="eq-importar-catalogo-btn" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border bg-white p-0.5" data-testid="eq-filtro">
          {FILTROS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFiltro(k)} data-testid={`eq-filtro-${k}`}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${filtro === k ? 'bg-orange-500 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{label}</button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 w-80" placeholder="Buscar por código o nombre TUPU" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="eq-search" />
        </div>
        <span className="text-xs text-slate-400">{rows.length} productos</span>
        <Button onClick={() => saveMany(dirtyIds)} disabled={!dirtyIds.length || saving} className="ml-auto bg-orange-500 hover:bg-orange-600" data-testid="eq-guardar-todo-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Guardando...' : `Guardar cambios (${dirtyIds.length})`}
        </Button>
      </div>
      <p className="text-xs text-slate-500">Factor = cuánto equivale 1 unidad TUPU en la unidad de Justo (ej. 1 caja de queso crema = 10 kg → 10; 2840 gr → kg → 2,84). Si las unidades coinciden se propone 1. En el selector: flechas para moverse, Enter para elegir, Tab para pasar al factor.</p>
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm" data-testid="eq-table">
          <thead className="bg-slate-50 text-slate-600"><tr>
            <th className="px-3 py-2 text-left">Código</th><th className="px-3 py-2 text-left">Producto TUPU</th><th className="px-3 py-2 text-left">Unidad</th>
            <th className="px-3 py-2 text-left">Ingrediente Justo</th><th className="px-3 py-2 text-left">Unidad Justo</th><th className="px-3 py-2 text-left">Factor</th>
            <th className="px-3 py-2 text-center">No se exporta</th><th className="px-3 py-2" />
          </tr></thead>
          <tbody>{rows.map((r) => {
            const v = edits[r.item_id] || base(r);
            return <EquivalenciaRow key={r.item_id} r={r} v={v} catalogo={catalogo} dirty={isDirty(r, edits[r.item_id])} saving={saving}
              onEdit={(nv) => setEdits((e) => ({ ...e, [r.item_id]: nv }))} onSave={() => saveMany([r.item_id])} />;
          })}</tbody>
        </table>
      </div>
      <UploadResult result={result} onClose={() => setResult(null)} />
    </div>
  );
};
